import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type {
  Organization,
  OrganizationDetail,
  OrganizationDomain,
  OrganizationMember,
  OrganizationInvitation,
  CreateOrganizationRequest,
  UpdateOrganizationRequest,
  InviteMemberRequest,
  UpdateMemberRoleRequest,
} from '@/features/organizations/types';
import { organizationsApi } from '@/features/organizations/api';
import { useNotification } from '@asabdev/asab-ui';
import { storage } from '@/lib/storage';

const CURRENT_ORG_ID_KEY = 'currentOrganizationId';

export const useOrganizationsStore = defineStore('organizations', () => {
  // State
  const organizations = ref<Organization[]>([]);
  const currentOrganizationId = ref<string | null>(null);
  const currentOrganizationDetail = ref<OrganizationDetail | null>(null);
  const membersCache = ref<Record<string, OrganizationMember[]>>({});
  const invitationsCache = ref<Record<string, OrganizationInvitation[]>>({});
  const domainsCache = ref<Record<string, OrganizationDomain[]>>({});
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  // Notification composable
  const { showSuccess, showError } = useNotification();

  // Getters
  const currentOrganization = computed(() => {
    if (!currentOrganizationId.value) return null;
    // Prefer detailed version if it's the current one
    if (currentOrganizationDetail.value?.id === currentOrganizationId.value) {
      return currentOrganizationDetail.value;
    }
    return organizations.value.find(org => org.id === currentOrganizationId.value) || null;
  });

  const currentMembers = computed(() => {
    if (!currentOrganizationId.value) return [];
    return membersCache.value[currentOrganizationId.value] || [];
  });

  const currentInvitations = computed(() => {
    if (!currentOrganizationId.value) return [];
    return invitationsCache.value[currentOrganizationId.value] || [];
  });

  const currentDomains = computed(() => {
    if (!currentOrganizationId.value) return [];
    return domainsCache.value[currentOrganizationId.value] || [];
  });

  const hasOrganizations = computed(() => organizations.value.length > 0);

  const pendingInvitationsCount = computed(() => {
    if (!currentOrganizationId.value) return 0;
    return (invitationsCache.value[currentOrganizationId.value] || []).length;
  });

  // Helper actions
  const setLoading = (loading: boolean) => {
    isLoading.value = loading;
  };

  const clearError = () => {
    error.value = null;
  };

  const handleApiError = (err: unknown, defaultMessage: string) => {
    const axiosErr = err as { response?: { data?: { detail?: string; error?: string } } };
    const errorMessage =
      axiosErr.response?.data?.error ||
      axiosErr.response?.data?.detail ||
      defaultMessage;
    error.value = errorMessage;
    showError(errorMessage);
    return errorMessage;
  };

  // Actions

  /**
   * Fetch all organizations for the current user
   */
  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      clearError();
      const response = await organizationsApi.listOrganizations();
      organizations.value = response;

      // If we have organizations but no current ID selected, or current ID not in list, select first
      if (organizations.value.length > 0) {
        if (
          !currentOrganizationId.value ||
          !organizations.value.find(org => org.id === currentOrganizationId.value)
        ) {
          setCurrentOrganization(organizations.value[0].id);
        }
      } else {
        currentOrganizationId.value = null;
      }
    } catch (err) {
      handleApiError(err, 'Failed to fetch organizations');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetch full details for the current organization
   */
  const fetchOrganizationDetail = async (id?: string) => {
    const orgId = id || currentOrganizationId.value;
    if (!orgId) return;

    try {
      setLoading(true);
      clearError();
      const response = await organizationsApi.getOrganization(orgId);
      currentOrganizationDetail.value = response;

      // Cache members and invitations if present in detail
      if (response.members) {
        membersCache.value[orgId] = response.members;
      }
    } catch (err) {
      handleApiError(err, 'Failed to fetch organization details');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Create a new organization
   */
  const createOrganization = async (data: CreateOrganizationRequest) => {
    try {
      setLoading(true);
      clearError();
      const response = await organizationsApi.createOrganization(data);
      organizations.value.push(response);
      showSuccess(`Organization "${response.name}" created successfully`);
      setCurrentOrganization(response.id);
      return response;
    } catch (err) {
      handleApiError(err, 'Failed to create organization');
      return null;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Update current organization
   */
  const updateOrganization = async (id: string, data: UpdateOrganizationRequest) => {
    try {
      setLoading(true);
      clearError();
      const response = await organizationsApi.updateOrganization(id, data);

      // Update detail if it's the current one (do this first to avoid stale computed values)
      if (currentOrganizationDetail.value?.id === id) {
        currentOrganizationDetail.value = {
          ...currentOrganizationDetail.value,
          ...response,
        };
      }

      // Update in list
      const index = organizations.value.findIndex(org => org.id === id);
      if (index !== -1) {
        organizations.value[index] = response;
      }

      showSuccess('Organization updated successfully');
      return response;
    } catch (err) {
      handleApiError(err, 'Failed to update organization');
      return null;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Delete an organization
   */
  const deleteOrganization = async (id: string) => {
    try {
      setLoading(true);
      clearError();
      await organizationsApi.deleteOrganization(id);

      organizations.value = organizations.value.filter(org => org.id !== id);

      if (currentOrganizationId.value === id) {
        currentOrganizationId.value =
          organizations.value.length > 0 ? organizations.value[0].id : null;
        currentOrganizationDetail.value = null;
      }

      showSuccess('Organization deleted successfully');
      return true;
    } catch (err) {
      handleApiError(err, 'Failed to delete organization');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // === Member Actions ===

  /**
   * Fetch members for an organization
   */
  const fetchMembers = async (id?: string) => {
    const orgId = id || currentOrganizationId.value;
    if (!orgId) return;

    try {
      setLoading(true);
      const response = await organizationsApi.listMembers(orgId);
      membersCache.value[orgId] = response;
    } catch (err) {
      handleApiError(err, 'Failed to fetch members');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Update a member's role
   */
  const updateMemberRole = async (userId: string, data: UpdateMemberRoleRequest) => {
    if (!currentOrganizationId.value) return;

    try {
      setLoading(true);
      const response = await organizationsApi.updateMemberRole(
        currentOrganizationId.value,
        userId,
        data
      );

      // Update cache
      const members = membersCache.value[currentOrganizationId.value];
      if (members) {
        const index = members.findIndex(m => m.user_id === userId);
        if (index !== -1) {
          members[index] = response;
        }
      }

      showSuccess('Member role updated successfully');
      return response;
    } catch (err) {
      handleApiError(err, 'Failed to update member role');
      return null;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Remove a member
   */
  const removeMember = async (userId: string) => {
    if (!currentOrganizationId.value) return;

    const orgId = currentOrganizationId.value;
    const previousMembers = [...(membersCache.value[orgId] || [])];

    try {
      // Optimistic update
      membersCache.value[orgId] = previousMembers.filter(m => m.user_id !== userId);

      await organizationsApi.removeMember(orgId, userId);
      showSuccess('Member removed successfully');
      return true;
    } catch (err) {
      // Rollback
      membersCache.value[orgId] = previousMembers;
      handleApiError(err, 'Failed to remove member');
      return false;
    }
  };

  /**
   * Leave organization
   */
  const leaveOrganization = async (id?: string) => {
    const orgId = id || currentOrganizationId.value;
    if (!orgId) return;

    try {
      setLoading(true);
      await organizationsApi.leaveOrganization(orgId);

      organizations.value = organizations.value.filter(org => org.id !== orgId);
      if (currentOrganizationId.value === orgId) {
        currentOrganizationId.value =
          organizations.value.length > 0 ? organizations.value[0].id : null;
        currentOrganizationDetail.value = null;
      }

      showSuccess('You have left the organization');
      return true;
    } catch (err) {
      handleApiError(err, 'Failed to leave organization');
      return false;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Transfer organization ownership
   */
  const transferOwnership = async (newOwnerId: string, password: string) => {
    if (!currentOrganizationId.value) return;

    try {
      setLoading(true);
      await organizationsApi.transferOwnership(currentOrganizationId.value, {
        new_owner_id: newOwnerId,
        password: password,
      });

      showSuccess('Ownership transferred successfully');
      // Refresh organization detail to get updated roles
      await fetchOrganizationDetail();
      return true;
    } catch (err) {
      handleApiError(err, 'Failed to transfer ownership');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // === Invitation Actions ===

  /**
   * Fetch invitations for an organization
   */
  const fetchInvitations = async (id?: string) => {
    const orgId = id || currentOrganizationId.value;
    if (!orgId) return;

    try {
      setLoading(true);
      const response = await organizationsApi.listInvitations(orgId);
      invitationsCache.value[orgId] = response;
    } catch (err) {
      handleApiError(err, 'Failed to fetch invitations');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Create an invitation
   */
  const createInvitation = async (data: InviteMemberRequest) => {
    if (!currentOrganizationId.value) return;

    try {
      setLoading(true);
      const response = await organizationsApi.createInvitation(currentOrganizationId.value, data);

      if (!invitationsCache.value[currentOrganizationId.value]) {
        invitationsCache.value[currentOrganizationId.value] = [];
      }
      invitationsCache.value[currentOrganizationId.value].push(response);

      showSuccess(`Invitation sent to ${data.email}`);
      return response;
    } catch (err) {
      handleApiError(err, 'Failed to send invitation');
      return null;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Cancel an invitation
   */
  const cancelInvitation = async (inviteId: string) => {
    if (!currentOrganizationId.value) return;

    try {
      setLoading(true);
      await organizationsApi.cancelInvitation(currentOrganizationId.value, inviteId);

      if (invitationsCache.value[currentOrganizationId.value]) {
        invitationsCache.value[currentOrganizationId.value] = invitationsCache.value[
          currentOrganizationId.value
        ].filter(inv => inv.id !== inviteId);
      }

      showSuccess('Invitation cancelled');
      return true;
    } catch (err) {
      handleApiError(err, 'Failed to cancel invitation');
      return false;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Accept an invitation
   */
  const acceptInvitation = async (inviteId: string) => {
    try {
      setLoading(true);
      await organizationsApi.acceptInvitation(inviteId);
      showSuccess('Invitation accepted successfully');
      // Refresh organizations after accepting
      await fetchOrganizations();
      return true;
    } catch (err) {
      handleApiError(err, 'Failed to accept invitation');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // === Context & Persistence ===

  /**
   * Set current organization and persist choice
   */
  const setCurrentOrganization = (id: string | null) => {
    currentOrganizationId.value = id;
    if (id) {
      storage.setItem(CURRENT_ORG_ID_KEY, id);
      fetchOrganizationDetail(id);
      fetchInvitations(id);
    } else {
      storage.removeItem(CURRENT_ORG_ID_KEY);
      currentOrganizationDetail.value = null;
    }
  };

  /**
   * Initialize store
   */
  const initialize = async () => {
    const savedId = storage.getItem(CURRENT_ORG_ID_KEY);
    if (savedId) {
      currentOrganizationId.value = savedId;
      fetchInvitations(savedId);
    }
    await fetchOrganizations();
  };

  /**
   * Clear state on logout
   */
  const clear = () => {
    organizations.value = [];
    currentOrganizationId.value = null;
    currentOrganizationDetail.value = null;
    membersCache.value = {};
    invitationsCache.value = {};
    storage.removeItem(CURRENT_ORG_ID_KEY);
  };

  /**
   * Fetch domains for an organization
   */
  const fetchDomains = async (id?: string) => {
    const orgId = id || currentOrganizationId.value;
    if (!orgId) return;

    try {
      const domains = await organizationsApi.listDomains(orgId);
      domainsCache.value[orgId] = domains;
    } catch {
      // Silently fail — domains may not exist yet
      domainsCache.value[orgId] = [];
    }
  };

  /**
   * Add a new domain to an organization
   */
  const addDomain = async (id: string, domain: string) => {
    try {
      setLoading(true);
      const response = await organizationsApi.addDomain(id, domain);

      // Refresh domains list
      await fetchDomains(id);

      showSuccess(`Domain ${domain} added successfully`);
      return response;
    } catch (err) {
      handleApiError(err, 'Failed to add domain');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Verify an organization domain
   */
  const verifyDomain = async (id: string, domainId: string) => {
    try {
      setLoading(true);
      const response = await organizationsApi.verifyDomain(id, domainId);

      // Refresh organization detail to get updated verification status
      await fetchOrganizationDetail(id);

      if (response.is_verified) {
        showSuccess(`Domain ${response.domain} verified successfully`);
      }
      return response.is_verified;
    } catch (err) {
      handleApiError(err, 'Failed to verify domain');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Remove a domain from an organization
   */
  const removeDomain = async (id: string, domainId: string) => {
    try {
      setLoading(true);
      await organizationsApi.removeDomain(id, domainId);

      // Refresh domains list
      await fetchDomains(id);

      showSuccess('Domain removed successfully');
      return true;
    } catch (err) {
      handleApiError(err, 'Failed to remove domain');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    // State
    organizations,
    currentOrganizationId,
    currentOrganizationDetail,
    membersCache,
    invitationsCache,
    isLoading,
    error,

    // Getters
    currentOrganization,
    currentMembers,
    currentInvitations,
    pendingInvitationsCount,
    hasOrganizations,

    // Actions
    initialize,
    fetchOrganizations,
    fetchOrganizationDetail,
    createOrganization,
    updateOrganization,
    deleteOrganization,
    setCurrentOrganization,
    fetchMembers,
    updateMemberRole,
    removeMember,
    leaveOrganization,
    transferOwnership,
    fetchInvitations,
    createInvitation,
    cancelInvitation,
    acceptInvitation,
    currentDomains,
    fetchDomains,
    addDomain,
    verifyDomain,
    removeDomain,
    clear,
  };
});
